import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Form, Input, Select, DatePicker, InputNumber, message } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';
import { postData, putData } from '../../../actions/developer';
import { getData } from '../../../actions/apiCall';
import { getAccess } from '../../../actions/auth';
import axios from 'axios';

const { TextArea } = Input;
const { Option } = Select;

interface AddInterviewModalProps {
    visible: boolean;
    onClose: () => void;
    onSuccess: () => void;
    initialDate?: Dayjs;
    editData?: any;
    isEditMode?: boolean;
}

interface User {
    _id: string;
    firstName: string;
    lastName: string;
    employeeId?: string;
}

interface Deal {
    _id: string;
    dealName: string;
}

interface Developer {
    _id: string;
    firstName: string;
    lastName: string;
    employeeId?: string;
}

interface FormFieldConfig {
    name: string;
    label: string;
    type: 'select' | 'input' | 'textarea' | 'datepicker' | 'number';
    required?: boolean;
    placeholder?: string;
    span?: number;
    options?: { value: string; label: string }[];
    optionsSource?: 'users' | 'deals' | 'developers';
    loading?: boolean;
    rows?: number;
    min?: number;
    format?: string;
    showTime?: boolean;
}

const AddInterviewModal: React.FC<AddInterviewModalProps> = ({
    visible,
    onClose,
    onSuccess,
    initialDate,
    editData,
    isEditMode = false
}) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState<User[]>([]);
    const [deals, setDeals] = useState<Deal[]>([]);
    const [developers, setDevelopers] = useState<Developer[]>([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [loadingDeals, setLoadingDeals] = useState(false);
    const [loadingDevelopers, setLoadingDevelopers] = useState(false);
    const [interviewLevels, setInterviewLevels] = useState<{ value: string; label: string }[]>([]);
    const [loadingLevels, setLoadingLevels] = useState(false);

    // Fetch interview levels from API
    useEffect(() => {
        const fetchInterviewLevels = async () => {
            setLoadingLevels(true);
            try {
                const response = await dispatch(getData('/api/calendar/interviews/levels'));
                if (response?.success && response?.data) {
                    setInterviewLevels(response.data);
                } else if (response?.code === 200 && response?.data) {
                    setInterviewLevels(response.data);
                }
            } catch (error) {
                console.error('Error fetching interview levels:', error);
                // Fallback to default levels if API fails
                setInterviewLevels([
                    { value: 'Screening', label: 'Screening' },
                    { value: 'Coding', label: 'Coding' },
                    { value: 'Technical', label: 'Technical' },
                    { value: 'Level 1', label: 'Level 1' },
                    { value: 'Level 2', label: 'Level 2' },
                    { value: 'Level 3', label: 'Level 3' },
                    { value: 'HR', label: 'HR' },
                    { value: 'Manager', label: 'Manager' },
                    { value: 'Final', label: 'Final' }
                ]);
            } finally {
                setLoadingLevels(false);
            }
        };
        fetchInterviewLevels();
    }, []);

    useEffect(() => {
        if (visible) {
            if (isEditMode && editData) {
                // Populate form with edit data
                let interviewDate = null;
                if (editData.interview_datetime) {
                    const dateValue = editData.interview_datetime;
                    // Handle date parsing - MongoDB stores dates in UTC but we want to display as local time
                    // The backend stores dates treating the input as local time, so we need to extract
                    // UTC components and use them as if they were local time components
                    if (dateValue instanceof Date) {
                        // If it's a Date object from MongoDB (stored in UTC)
                        // Extract UTC components and create a local date to preserve the original time
                        const utcYear = dateValue.getUTCFullYear();
                        const utcMonth = dateValue.getUTCMonth();
                        const utcDate = dateValue.getUTCDate();
                        const utcHour = dateValue.getUTCHours();
                        const utcMinute = dateValue.getUTCMinutes();
                        const utcSecond = dateValue.getUTCSeconds();
                        // Create a dayjs object using UTC components as local time
                        interviewDate = dayjs().year(utcYear).month(utcMonth).date(utcDate)
                            .hour(utcHour).minute(utcMinute).second(utcSecond).millisecond(0);
                    } else if (typeof dateValue === 'string') {
                        // Parse the string first to get a date object
                        const tempDate = new Date(dateValue);
                        if (isNaN(tempDate.getTime())) {
                            // If parsing fails, try dayjs directly
                            interviewDate = dayjs(dateValue);
                        } else {
                            // Extract UTC components and use them as local time
                            // This preserves the original time that was entered
                            const utcYear = tempDate.getUTCFullYear();
                            const utcMonth = tempDate.getUTCMonth();
                            const utcDate = tempDate.getUTCDate();
                            const utcHour = tempDate.getUTCHours();
                            const utcMinute = tempDate.getUTCMinutes();
                            const utcSecond = tempDate.getUTCSeconds();
                            // Create a dayjs object using UTC components as local time
                            interviewDate = dayjs().year(utcYear).month(utcMonth).date(utcDate)
                                .hour(utcHour).minute(utcMinute).second(utcSecond).millisecond(0);
                        }
                    } else {
                        // Fallback
                        interviewDate = dayjs(dateValue);
                    }
                } else if (initialDate && initialDate.isValid()) {
                    interviewDate = initialDate;
                }

                form.setFieldsValue({
                    interviewOwner: editData.interviewOwner?._id || editData.interviewOwner,
                    deal: editData.deal?._id || editData.deal,
                    developer: editData.developer?._id || editData.developer,
                    interviewTitle: editData.interviewTitle,
                    interview_type: editData.interview_type,
                    interview_datetime: interviewDate,
                    duration: editData.duration,
                    interview_link: editData.interview_link,
                    job_description: editData.job_description,
                    instructions: editData.instructions
                });
            } else {
                form.resetFields();
                if (initialDate && initialDate.isValid()) {
                    // Ensure the date is properly set with time
                    // Clone to avoid mutating the original
                    const dateToSet = initialDate.clone();
                    form.setFieldsValue({
                        interview_datetime: dateToSet
                    });
                }
            }
            fetchUsers();
            fetchDeals();
            fetchDevelopers();
        }
    }, [visible, initialDate, isEditMode, editData]);

    const fetchUsers = async () => {
        setLoadingUsers(true);
        try {
            axios.defaults.headers.common['permissions'] = JSON.stringify(getAccess());
            const response = await dispatch(getData('/users', { limit: 100 }));
            if (response?.code === 200 && response?.data) {
                setUsers(response.data);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setLoadingUsers(false);
        }
    };

    const fetchDeals = async () => {
        setLoadingDeals(true);
        try {
            axios.defaults.headers.common['permissions'] = JSON.stringify(getAccess());
            const response = await dispatch(getData('/deals', { limit: 100 }));
            if (response?.code === 200 && response?.data) {
                setDeals(response.data);
            }
        } catch (error) {
            console.error('Error fetching deals:', error);
        } finally {
            setLoadingDeals(false);
        }
    };

    const fetchDevelopers = async () => {
        setLoadingDevelopers(true);
        try {
            axios.defaults.headers.common['permissions'] = JSON.stringify(getAccess());
            const response = await dispatch(getData('/bench-developers', { limit: 100 }));
            if (response?.code === 200 && response?.data) {
                setDevelopers(response.data);
            }
        } catch (error) {
            console.error('Error fetching developers:', error);
        } finally {
            setLoadingDevelopers(false);
        }
    };

    const handleSubmit = async (values: any) => {
        setLoading(true);
        try {
            // Format interview_datetime - preserve local time without timezone conversion
            let interview_datetime = null;
            if (values.interview_datetime) {
                const dt = values.interview_datetime;
                // Create ISO string in local timezone format (without Z suffix)
                // Format: YYYY-MM-DDTHH:mm:ss (local time, backend will interpret correctly)
                const year = dt.year();
                const month = String(dt.month() + 1).padStart(2, '0');
                const day = String(dt.date()).padStart(2, '0');
                const hour = String(dt.hour()).padStart(2, '0');
                const minute = String(dt.minute()).padStart(2, '0');
                const second = String(dt.second()).padStart(2, '0');
                // Send without timezone indicator - backend should interpret as local/server time
                interview_datetime = `${year}-${month}-${day}T${hour}:${minute}:${second}`;
            }

            const payload = {
                interviewOwner: values.interviewOwner,
                deal: values.deal,
                developer: values.developer,
                interviewTitle: values.interviewTitle,
                interview_type: values.interview_type,
                interview_datetime: interview_datetime,
                duration: values.duration ? parseInt(values.duration) : null,
                interview_link: values.interview_link,
                job_description: values.job_description,
                instructions: values.instructions,
                ...(isEditMode ? {} : { interviewStatus: 'Inprogress', status: 'active' })
            };

            axios.defaults.headers.common['permissions'] = JSON.stringify(getAccess());

            let response;
            if (isEditMode && editData?._id) {
                response = await dispatch(putData(`/contract_interview/${editData._id}`, payload));
                if (response?.code === 200 || response?._id) {
                    message.success('Interview updated successfully!');
                    form.resetFields();
                    onSuccess();
                    onClose();
                } else {
                    message.error(response?.message || 'Failed to update interview');
                }
            } else {
                response = await dispatch(postData('/contract_interview', payload));
                if (response?.code === 200) {
                    message.success('Interview scheduled successfully!');
                    form.resetFields();
                    onSuccess();
                    onClose();
                } else {
                    message.error(response?.message || 'Failed to schedule interview');
                }
            }
        } catch (error: any) {
            console.error(`Error ${isEditMode ? 'updating' : 'creating'} interview:`, error);
            // Check if it's a conflict error (409 status code)
            if (error?.response?.status === 409 || error?.code === 409) {
                message.error(error?.response?.data?.message || error?.message || 'Interview scheduling conflict: Developer already has an interview at this time.');
            } else {
                message.error(error?.response?.data?.message || error?.message || `Failed to ${isEditMode ? 'update' : 'schedule'} interview`);
            }
        } finally {
            setLoading(false);
        }
    };

    // Form fields configuration
    const formFields: FormFieldConfig[] = useMemo(() => [
        {
            name: 'interviewOwner',
            label: 'Interview Owner',
            type: 'select',
            required: true,
            placeholder: 'Select Interview Owner',
            span: 12,
            optionsSource: 'users',
            loading: loadingUsers
        },
        {
            name: 'deal',
            label: 'Deal',
            type: 'select',
            required: true,
            placeholder: 'Select Deal',
            span: 12,
            optionsSource: 'deals',
            loading: loadingDeals
        },
        {
            name: 'developer',
            label: 'Developer',
            type: 'select',
            required: true,
            placeholder: 'Select Developer',
            span: 12,
            optionsSource: 'developers',
            loading: loadingDevelopers
        },
        {
            name: 'interviewTitle',
            label: 'Interview Title',
            type: 'input',
            required: true,
            placeholder: 'Enter interview title',
            span: 12
        },
        {
            name: 'interview_type',
            label: 'Interview Type',
            type: 'select',
            required: true,
            placeholder: 'Select Interview Type',
            span: 12,
            options: interviewLevels
        },
        {
            name: 'interview_datetime',
            label: 'Interview Date & Time',
            type: 'datepicker',
            required: true,
            placeholder: 'Select date and time',
            span: 12,
            showTime: true,
            format: 'DD-MM-YYYY HH:mm A'
        },
        {
            name: 'duration',
            label: 'Duration (Minutes)',
            type: 'number',
            placeholder: 'Enter duration in minutes',
            span: 12,
            min: 1
        },
        {
            name: 'interview_link',
            label: 'Interview Link',
            type: 'input',
            required: true,
            placeholder: 'Enter interview meeting link',
            span: 12
        },
        {
            name: 'job_description',
            label: 'Job Description',
            type: 'textarea',
            placeholder: 'Enter job description',
            span: 24,
            rows: 4
        },
        {
            name: 'instructions',
            label: 'Instructions',
            type: 'textarea',
            placeholder: 'Enter any additional instructions',
            span: 24,
            rows: 3
        }
    ], [loadingUsers, loadingDeals, loadingDevelopers, interviewLevels]);

    // Get options for select fields
    const getOptions = (source: 'users' | 'deals' | 'developers') => {
        switch (source) {
            case 'users':
                return users.map((user) => ({
                    value: user._id,
                    label: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.employeeId || user._id
                }));
            case 'deals':
                return deals.map((deal) => ({
                    value: deal._id,
                    label: deal.dealName
                }));
            case 'developers':
                return developers.map((dev) => ({
                    value: dev._id,
                    label: `${dev.firstName || ''} ${dev.lastName || ''}`.trim() || dev.employeeId || dev._id
                }));
            default:
                return [];
        }
    };

    // Render form field based on configuration
    const renderFormField = (field: FormFieldConfig) => {
        const rules = field.required
            ? [{ required: true, message: `Please ${field.type === 'select' ? 'select' : 'enter'} ${field.label.toLowerCase()}` }]
            : [];

        const commonProps = {
            placeholder: field.placeholder,
            style: { width: '100%' }
        };

        let fieldComponent;
        switch (field.type) {
            case 'select':
                if (field.optionsSource) {
                    const options = getOptions(field.optionsSource);
                    fieldComponent = (
                        <Select
                            {...commonProps}
                            loading={field.loading}
                            showSearch
                            filterOption={(input, option) => {
                                const label = option?.label || '';
                                return String(label || '').toLowerCase().includes(input.toLowerCase());
                            }}
                            options={options}
                        />
                    );
                } else if (field.options) {
                    fieldComponent = (
                        <Select
                            {...commonProps}
                            options={field.options}
                        />
                    );
                }
                break;
            case 'input':
                fieldComponent = <Input {...commonProps} />;
                break;
            case 'textarea':
                fieldComponent = <TextArea {...commonProps} rows={field.rows || 3} />;
                break;
            case 'datepicker':
                fieldComponent = (
                    <DatePicker
                        {...commonProps}
                        showTime={field.showTime}
                        format={field.format}
                    />
                );
                break;
            case 'number':
                fieldComponent = (
                    <InputNumber
                        {...commonProps}
                        min={field.min}
                    />
                );
                break;
            default:
                fieldComponent = <Input {...commonProps} />;
        }

        return (
            <Form.Item
                key={field.name}
                name={field.name}
                label={field.label}
                rules={rules}
                style={{ marginBottom: field.span === 24 ? '16px' : '0' }}
            >
                {fieldComponent}
            </Form.Item>
        );
    };

    const handleCancel = () => {
        form.resetFields();
        onClose();
    };

    // Group fields by row (span 24 = full width, others = half width)
    const gridFields = formFields.filter(f => f.span === 12);
    const fullWidthFields = formFields.filter(f => f.span === 24);

    return (
        <Modal
            title={isEditMode ? "Edit Interview" : "Schedule Interview"}
            open={visible}
            onCancel={handleCancel}
            onOk={() => form.submit()}
            confirmLoading={loading}
            width={800}
            maskClosable={false}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{
                    interviewStatus: 'Inprogress'
                }}
            >
                <div className="grid grid-cols-2 gap-4">
                    {gridFields.map(renderFormField)}
                </div>
                {fullWidthFields.map(renderFormField)}
            </Form>
        </Modal>
    );
};

export default AddInterviewModal;
